
import { useEffect } from 'react';
import { usePermissionsStore } from '@/store/permissions.store';
import { useMemberPermissionsQuery } from '@/hooks/queries/usePermissionsQuery';
import { useOrganizationStore } from '@/store/organization.store';
import { useAuth } from '@/store/auth.store';
import { ModuleTypeEnum, PermissionActionEnum } from '@scrambl/shared';

/**
 * Custom hook for managing permissions in components
 * For external org access, permissions are checked via the permission set on the relationship
 */
export function usePermissions() {
  const {
    hasPermission
  } = usePermissionsStore();

  // Subscribe to externalOrgRelationship directly so the hook re-renders when it changes
  const currentOrganization = useOrganizationStore(state => state.currentOrganization);
  const externalOrgRelationship = useOrganizationStore(state => state.externalOrgRelationship);
  const getCurrentUserMembership = useOrganizationStore(state => state.getCurrentUserMembership);
  const { user } = useAuth();
  const isExternal = currentOrganization?.isExternalAccess === true;

  const currentMembership = getCurrentUserMembership();

  // Organization owners and admins don't need to fetch permissions - they have all permissions
  // Check both the getter and direct access for reliability during initialization
  const memberRole = (currentMembership?.role || currentOrganization?.userMembership?.role)?.toUpperCase();
  const isAdminOrOwner = memberRole === 'OWNER' || memberRole === 'ADMIN';

  // Get membership ID from both sources for reliability
  const effectiveMembershipId = currentMembership?.id || currentOrganization?.userMembership?.id;

  // Fetch current user's permissions using query hook (only for non-admin/owner members)
  // Disabled for external org access - permissions are handled server-side via permission set
  const requiresMemberPermissions =
    !!currentOrganization?.id &&
    !!effectiveMembershipId &&
    !isAdminOrOwner &&
    !isExternal;

  const {
    data: memberPermissionsData,
    isPending: isMemberPermissionsPending,
  } = useMemberPermissionsQuery(
    effectiveMembershipId,
    { enabled: requiresMemberPermissions }
  );

  // `isLoading` excludes restore-paused queries. Treat a required permission
  // query as loading for its entire pending state so route/page denial checks
  // cannot act on an empty permission map during cache hydration. Disabled
  // queries (admins, owners, and external users) remain ready immediately.
  const isLoadingMemberPermissions =
    requiresMemberPermissions && isMemberPermissionsPending;

  const memberPermissions = memberPermissionsData || null;

  // Update store with memberPermissions for hasPermission to work
  // Only update if the data actually changed to prevent unnecessary re-renders
  useEffect(() => {
    if (memberPermissionsData && effectiveMembershipId) {
      const currentStorePermissions = usePermissionsStore.getState().memberPermissions;
      // Only update if the permissions data is different (compare by stringifying or checking reference)
      // Using JSON.stringify for deep comparison since the object structure may be complex
      const newPermissionsStr = JSON.stringify(memberPermissionsData);
      const currentPermissionsStr = currentStorePermissions ? JSON.stringify(currentStorePermissions) : '';

      if (newPermissionsStr !== currentPermissionsStr) {
        usePermissionsStore.setState({ memberPermissions: memberPermissionsData as any });
      }
    }
  }, [memberPermissionsData, effectiveMembershipId]);

  /**
   * Check if the current user has a specific permission
   * Uses query data directly instead of store to avoid timing issues
   * For external org access, permissions are checked server-side via permission set
   */
  const checkPermission = (module: ModuleTypeEnum, action: PermissionActionEnum): boolean => {
    // System admins have all permissions
    if (user?.role === 'SYSTEM_ADMIN' || user?.role === 'ADMIN') {
      return true;
    }

    // If no current organization info is available, default to FALSE during initial load
    // This prevents showing modules before org context is established
    if (!currentOrganization?.id) {
      return false;
    }

    // Organization owners and admins have all permissions within their org
    if (isAdminOrOwner) {
      return true;
    }

    // For external org access, check permissions from the external relationship's permission set
    if (isExternal) {
      // If no relationship or no permission set, deny access
      if (!externalOrgRelationship?.permissionSet) {
        return false;
      }

      // Check permissions via the external org's permission set
      const permissionSet = externalOrgRelationship.permissionSet;
      return permissionSet.permissionSetItems?.some(
        item => item.permissionDefinition?.moduleType === module &&
                item.permissionDefinition?.allowedActions?.includes(action)
      ) || false;
    }

    // If membership is still loading (no membership ID yet), deny access
    // This prevents showing modules before membership is established
    // Check both getter and direct access for reliability
    const membershipId = currentMembership?.id || currentOrganization?.userMembership?.id;
    if (!membershipId) {
      return false;
    }

    // If permissions are still loading, default to FALSE to hide navigation items
    // This prevents showing modules the user doesn't have access to during load
    if (isLoadingMemberPermissions) {
      return false;
    }

    // Check permissions directly from query data to avoid store timing issues
    // The useEffect that updates the store runs after render, so we need to check the data directly
    if (memberPermissionsData) {
      // Check direct permissions
      const hasDirectPermission = memberPermissionsData.directPermissions?.some(
        perm => perm.permissionDefinition?.moduleType === module &&
                perm.permissionDefinition?.allowedActions?.includes(action)
      ) || false;

      if (hasDirectPermission) {
        return true;
      }

      // Check permissions via permission sets
      const hasSetPermission = memberPermissionsData.permissionSets?.some(
        setAssignment => setAssignment.permissionSet?.permissionSetItems?.some(
          item => item.permissionDefinition?.moduleType === module &&
                  item.permissionDefinition?.allowedActions?.includes(action)
        )
      ) || false;

      return hasSetPermission;
    }

    // Fallback to store-based check (for backwards compatibility)
    return hasPermission(module, action);
  };

  /**
   * Check if the current user can perform any of the given actions on a module
   */
  const checkAnyPermission = (module: ModuleTypeEnum, actions: PermissionActionEnum[]): boolean => {
    return actions.some(action => checkPermission(module, action));
  };

  /**
   * Check if the current user can perform all of the given actions on a module
   */
  const checkAllPermissions = (module: ModuleTypeEnum, actions: PermissionActionEnum[]): boolean => {
    return actions.every(action => checkPermission(module, action));
  };

  return {
    checkPermission,
    checkAnyPermission,
    checkAllPermissions,
    isLoadingPermissions: isLoadingMemberPermissions,
    // Expose the raw hasPermission for advanced use cases
    hasPermission,
  };
}
